import { z } from 'zod';
import { ApprovalPolicies, CampaignStatuses, Channels, ChannelPolicies, GenerationModes, MessagePurposes, MessageStatuses, SendResults, SkipReasons, TargetStates, TrackingEventTypes, } from '../statuses';
import { LIMITS, listQuerySchema, optionalTrimmed, trimmed, uuid, csvArray } from './common';
const enumOf = (values) => z.enum(values);
/** 文字列リテラル型を保つ必要がある項目は個別に定義する */
const channelEnum = z.enum(['form', 'email']);
const purposeEnum = z.enum(['initial', 'followup_1', 'followup_2', 'reply']);
const generationModeEnum = z.enum(['ai', 'template', 'template_ai']);
/** 文面の制約（docs/02-functional-spec.md F06.2） */
export const MESSAGE_LIMITS = {
    subjectRecommended: 30,
    subjectMax: 40,
    emailBodyMin: 400,
    emailBodyMax: 800,
    formBodyMax: 500,
    bodyHardMax: 1200,
};
// ---------------------------------------------------------------- templates
export const templateVariableSchema = z.object({
    key: z.string().regex(/^[a-z_][a-z0-9_]*$/i, '英数字とアンダースコアで指定してください').max(40),
    label: trimmed(40),
    sample: optionalTrimmed(200),
});
export const createTemplateSchema = z.object({
    name: trimmed(80, 'テンプレート名を入力してください'),
    channel: channelEnum,
    subjectTemplate: optionalTrimmed(LIMITS.subject),
    bodyTemplate: trimmed(LIMITS.body, '本文を入力してください'),
    variables: z.array(templateVariableSchema).max(50).optional(),
    productId: uuid.optional(),
    isShared: z.boolean().default(true),
});
export const templateSchema = createTemplateSchema.extend({
    id: uuid,
    createdBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** 実績（文面改善の比較に使う） */
    stats: z
        .object({ sentCompanies: z.number().int(), repliedCompanies: z.number().int(), replyRate: z.number().nullable() })
        .nullable()
        .optional(),
});
/** 差し込みは {{company_name}} 形式 */
export const TEMPLATE_VARIABLES = [
    { key: 'company_name', label: '企業名', sample: '株式会社サンプル商事' },
    { key: 'company_industry', label: '業種', sample: '食品卸' },
    { key: 'company_region', label: '地域', sample: '東京都渋谷区' },
    { key: 'company_url', label: '企業サイト URL', sample: 'https://www.example.co.jp/' },
    { key: 'product_name', label: '商材名', sample: '食材卸サービス' },
    { key: 'sender_company', label: '自社名', sample: '株式会社ネスク' },
    { key: 'sender_name', label: '担当者名', sample: '田中' },
    { key: 'sender_contact', label: '連絡先', sample: 'sales@example.co.jp' },
    { key: 'scheduling_url', label: '日程調整 URL', sample: 'https://example.com/schedule' },
    { key: 'newsletter_url', label: 'メルマガ登録 URL', sample: 'https://example.com/newsletter' },
    // 以下はセキュリティチェック（F12）の直近の結果から入る。未実施の企業では埋まらない
    { key: 'security_risk_level', label: 'セキュリティ評価', sample: '中' },
    { key: 'security_severity', label: '所見の重要度', sample: '高' },
    { key: 'security_finding_title', label: '最重要の所見', sample: 'PHP 5.6.40: サポート終了（EOL）・既知脆弱性が未修正の状態' },
    { key: 'security_evidence', label: '所見の観測根拠', sample: 'X-Powered-By: PHP/5.6.40' },
    { key: 'security_risk_summary', label: '考えられるリスク', sample: 'サポート終了後に公開された脆弱性が修正されないまま残っている可能性があります' },
    { key: 'security_recommendation', label: '推奨対応', sample: 'サポートが継続しているバージョンへの更新をご検討ください' },
    // 所見を平易な文にしたもの。題名・重要度を載せずに「何が・なぜ・どうする」で書く
    { key: 'security_heading', label: '所見の見出し（平易）', sample: 'サポートが終了したソフトウェアの使用' },
    {
        key: 'security_explanation',
        label: '所見の説明（平易・3 段落）',
        sample: '【サポートが終了したソフトウェアの使用】\n貴社Webサイトで使用されているソフトウェア（PHP 5.6.40）が、提供元によるサポートが終了したバージョンであることが確認されました。\n\nサポート終了後に見つかった弱点は修正が提供されないため、公開されている情報をもとに攻撃を受けやすい状態が続きます。\n\n対策として、サポートが継続しているバージョンへの更新を推奨いたします。',
    },
    {
        key: 'security_pitch',
        label: '所見に合わせた NeSC の案内',
        sample: '今回のようなバージョンの指摘についても、実際にそのバージョンで悪用可能な弱点が残っているのかまで確認することが可能です。',
    },
];
/** 差し込みの書式。半角英数字とアンダースコアのキーだけを扱う */
const PLACEHOLDER_PATTERN = /\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi;
/** 置き換えられずに残った差し込み項目のキー（重複なし・出現順） */
export const findUnresolvedPlaceholders = (text) => [
    ...new Set([...text.matchAll(PLACEHOLDER_PATTERN)].map((m) => m[1])),
];
export const renderTemplate = (template, values) => template.replace(PLACEHOLDER_PATTERN, (match, key) => {
    const value = values[key];
    return value == null || value === '' ? match : value;
});
// ---------------------------------------------------------------- generated messages
export const rationaleSchema = z.object({
    productElements: z.array(z.string()).default([]),
    companyFacts: z
        .array(z.object({
        field: z.string(),
        value: z.string(),
        confidence: z.enum(['high', 'medium', 'low']),
        sourceUrl: z.string().nullable().optional(),
    }))
        .default([]),
    /** 推測を含む箇所。空でなければ画面に「推測を含む」を表示する */
    cautions: z.array(z.string()).default([]),
    model: z.string().optional(),
    promptVersion: z.string().optional(),
});
export const generatedMessageSchema = z.object({
    id: uuid,
    companyId: uuid,
    companyName: z.string(),
    campaignId: uuid.nullable(),
    campaignName: z.string().nullable(),
    productId: uuid.nullable(),
    templateId: uuid.nullable(),
    purpose: enumOf(MessagePurposes.values),
    channel: enumOf(Channels.values),
    subject: z.string().nullable(),
    body: z.string(),
    status: enumOf(MessageStatuses.values),
    generatedByAi: z.boolean(),
    aiRunId: uuid.nullable(),
    rationale: rationaleSchema.nullable(),
    version: z.number().int(),
    editedBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    editedAt: z.string().nullable(),
    approvedBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    approvedAt: z.string().nullable(),
    /** 禁止表現の検査結果。空でなければ画面に警告を出す */
    ngPhraseHits: z.array(z.string()).default([]),
    /** 送信時に使う宛先（メールアドレスまたはフォーム URL）。詳細でのみ返す。無ければ null */
    destination: z.object({ type: z.enum(['email', 'form']), value: z.string() }).nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const messageVersionSchema = z.object({
    id: uuid,
    version: z.number().int(),
    subject: z.string().nullable(),
    body: z.string(),
    source: z.enum(['ai', 'human']),
    createdAt: z.string(),
    createdBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
});
export const generateMessageSchema = z.object({
    companyId: uuid,
    productId: uuid,
    channel: channelEnum,
    purpose: purposeEnum.default('initial'),
    templateId: uuid.nullish(),
    tone: z.enum(['polite', 'concise']).default('polite'),
    maxLength: z.number().int().min(100).max(MESSAGE_LIMITS.bodyHardMax).optional(),
    campaignId: uuid.optional(),
    /** ai=AI 生成 / template=テンプレート差し込み */
    mode: generationModeEnum.optional(),
    /** フォーム送信時に URL を含めない設定（docs/02-functional-spec.md F06.3） */
    excludeUrls: z.boolean().optional(),
    /** 2 案を並べて比較する */
    alternatives: z.number().int().min(1).max(2).default(1),
});
export const bulkGenerateSchema = z.object({
    campaignId: uuid.optional(),
    listId: uuid.optional(),
    companyIds: z.array(uuid).max(5000).optional(),
    /** キャンペーン指定時はキャンペーンの商材を使う */
    productId: uuid.optional(),
    /** 省略時はキャンペーンの設定に従う */
    mode: generationModeEnum.optional(),
    channel: channelEnum.optional(),
    purpose: purposeEnum.default('initial'),
    templateId: uuid.nullish(),
    tone: z.enum(['polite', 'concise']).default('polite'),
    maxLength: z.number().int().min(100).max(MESSAGE_LIMITS.bodyHardMax).optional(),
});
export const updateMessageSchema = z.object({
    subject: optionalTrimmed(LIMITS.subject),
    body: z.string().trim().max(LIMITS.body).optional(),
});
export const messageListQuerySchema = listQuerySchema.extend({
    campaignId: uuid.optional(),
    companyId: uuid.optional(),
    status: csvArray(enumOf(MessageStatuses.values)),
    purpose: csvArray(enumOf(MessagePurposes.values)),
});
// ---------------------------------------------------------------- campaigns
export const allowedHoursSchema = z.object({
    /** 0=日曜。既定は平日 */
    daysOfWeek: z.array(z.number().int().min(0).max(6)).min(1).default([1, 2, 3, 4, 5]),
    startHour: z.number().int().min(0).max(23).default(9),
    endHour: z.number().int().min(1).max(24).default(18),
});
export const followupPolicySchema = z.object({
    enabled: z.boolean().default(false),
    /** 初回送信からの日数 */
    daysAfter: z.array(z.number().int().min(1).max(90)).max(2).default([7]),
    /** 返信があれば追客しない */
    stopOnReply: z.boolean().default(true),
    maxCount: z.number().int().min(0).max(2).default(1),
});
export const createCampaignSchema = z.object({
    name: trimmed(80, 'キャンペーン名を入力してください'),
    productId: uuid,
    listId: uuid,
    channelPolicy: enumOf(ChannelPolicies.values).default('form_first'),
    templateId: uuid.nullish(),
    /** template を選ぶ場合はテンプレートが必須（下の refine で確認する） */
    generationMode: enumOf(GenerationModes.values).default('ai'),
    approvalPolicy: enumOf(ApprovalPolicies.values).default('all'),
    scheduledAt: z.string().datetime().nullish(),
    allowedHours: allowedHoursSchema.default({ daysOfWeek: [1, 2, 3, 4, 5], startHour: 9, endHour: 18 }),
    dailyLimit: z.number().int().min(1).max(10000).default(100),
    totalLimit: z.number().int().min(1).max(100000).nullish(),
    domainIntervalHours: z.number().int().min(0).max(24 * 90).default(24),
    followupPolicy: followupPolicySchema.default({
        enabled: false,
        daysAfter: [7],
        stopOnReply: true,
        maxCount: 1,
    }),
    tone: z.enum(['polite', 'concise']).default('polite'),
    maxLength: z.number().int().min(100).max(MESSAGE_LIMITS.bodyHardMax).optional(),
});
export const updateCampaignSchema = createCampaignSchema.partial();
/** テンプレート差し込みは、差し込む元がないと成り立たない */
export const campaignTemplateRequired = (input) => (input.generationMode !== 'template' && input.generationMode !== 'template_ai') || !!input.templateId;
export const campaignCountsSchema = z.object({
    total: z.number().int(),
    done: z.number().int(),
    success: z.number().int(),
    failed: z.number().int(),
    unknown: z.number().int(),
    needsAction: z.number().int(),
    skipped: z.number().int(),
    pending: z.number().int(),
    holding: z.number().int(),
});
export const campaignSummarySchema = z.object({
    id: uuid,
    name: z.string(),
    status: enumOf(CampaignStatuses.values),
    product: z.object({ id: uuid, name: z.string() }).nullable(),
    list: z.object({ id: uuid, name: z.string() }).nullable(),
    channelPolicy: enumOf(ChannelPolicies.values),
    generationMode: enumOf(GenerationModes.values),
    approvalPolicy: enumOf(ApprovalPolicies.values),
    dailyLimit: z.number().int(),
    totalLimit: z.number().int().nullable(),
    /** 送信可能時間帯と 1 日あたり上限を無視して送っている */
    sendImmediately: z.boolean().default(false),
    counts: campaignCountsSchema,
    todaySent: z.number().int(),
    nextRunAt: z.string().nullable(),
    /** 実績（観測期間の注記つきで表示する） */
    repliedCompanies: z.number().int(),
    appointmentCompanies: z.number().int(),
    replyRate: z.number().nullable(),
    measuringCount: z.number().int(),
    scheduledAt: z.string().nullable(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    stopReason: z.string().nullable(),
    createdBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const campaignDetailSchema = campaignSummarySchema.extend({
    templateId: uuid.nullable(),
    generationMode: enumOf(GenerationModes.values),
    tone: z.enum(['polite', 'concise']),
    allowedHours: allowedHoursSchema,
    domainIntervalHours: z.number().int(),
    followupPolicy: followupPolicySchema,
    channelBreakdown: z.object({ form: z.number().int(), email: z.number().int() }),
    approvedMessages: z.number().int(),
    totalMessages: z.number().int(),
});
/** 送信前チェック結果（docs/05-api-spec.md 第9章） */
export const preflightSchema = z.object({
    totalCandidates: z.number().int(),
    sendable: z.number().int(),
    blocked: z.array(z.object({
        reason: enumOf(SkipReasons.values),
        label: z.string(),
        count: z.number().int(),
    })),
    notApproved: z.number().int(),
    channelBreakdown: z.object({ form: z.number().int(), email: z.number().int() }),
    estimatedDays: z.number().int(),
    estimatedCostUsd: z.number(),
    /** start 時に条件が変わっていないか確認するためのハッシュ */
    confirmToken: z.string(),
    warnings: z.array(z.string()).default([]),
});
export const startCampaignSchema = z.object({
    confirmToken: z.string().min(8),
    scheduledAt: z.string().datetime().nullish(),
    /** 送信可能時間帯と 1 日あたり上限を無視して今すぐ全件送る */
    immediate: z.boolean().optional(),
});
/** 「今すぐ配信する」。下書き・予約中は開始、実行中・一時停止中は即時配信へ切り替える */
export const sendNowCampaignSchema = z.object({
    /** 下書き・予約中のときは送信前チェックの確認トークンが要る */
    confirmToken: z.string().min(8).optional(),
});
export const pauseCampaignSchema = z.object({
    reason: optionalTrimmed(LIMITS.reason),
});
export const campaignTargetSchema = z.object({
    id: uuid,
    companyId: uuid,
    companyName: z.string(),
    contactId: uuid.nullable(),
    contactValue: z.string().nullable(),
    channel: enumOf(Channels.values).nullable(),
    generatedMessageId: uuid.nullable(),
    messageStatus: enumOf(MessageStatuses.values).nullable(),
    state: enumOf(TargetStates.values),
    skipReason: enumOf(SkipReasons.values).nullable(),
    scheduledAt: z.string().nullable(),
    attemptCount: z.number().int(),
    lastAttemptAt: z.string().nullable(),
    lastResult: enumOf(SendResults.values).nullable(),
    lastErrorCode: z.string().nullable(),
    lastSendAttemptId: uuid.nullable(),
});
export const campaignTargetsQuerySchema = listQuerySchema.extend({
    state: csvArray(enumOf(TargetStates.values)),
    result: csvArray(enumOf(SendResults.values)),
});
// ---------------------------------------------------------------- send attempts
export const sendAttemptSchema = z.object({
    id: uuid,
    campaignId: uuid.nullable(),
    campaignName: z.string().nullable(),
    campaignTargetId: uuid.nullable(),
    companyId: uuid,
    companyName: z.string(),
    contactId: uuid.nullable(),
    contactValue: z.string().nullable(),
    generatedMessageId: uuid.nullable(),
    channel: enumOf(Channels.values),
    result: enumOf(SendResults.values),
    resultDetail: z.string().nullable(),
    errorCode: z.string().nullable(),
    errorLabel: z.string().nullable(),
    messageId: z.string().nullable(),
    requestPayload: z.record(z.string(), z.unknown()).nullable(),
    screenshotBeforeUrl: z.string().nullable(),
    screenshotAfterUrl: z.string().nullable(),
    responseSnapshot: z.string().nullable(),
    durationMs: z.number().int().nullable(),
    sentAt: z.string().nullable(),
    retriedFromId: uuid.nullable(),
    isSafeMode: z.boolean(),
    createdAt: z.string(),
});
export const sendAttemptsQuerySchema = listQuerySchema.extend({
    campaignId: uuid.optional(),
    companyId: uuid.optional(),
    result: csvArray(enumOf(SendResults.values)),
    channel: csvArray(enumOf(Channels.values)),
    from: z.string().optional(),
    to: z.string().optional(),
});
export const singleSendSchema = z.object({
    generatedMessageId: uuid,
    contactId: uuid.optional(),
    /** 重複送信の可能性を了承した場合のみ true */
    acknowledgeDuplicate: z.boolean().optional(),
});
export const markResultSchema = z.object({
    result: z.enum(['success', 'failed']),
    note: optionalTrimmed(LIMITS.reason),
});
export const emergencyStopSchema = z.object({
    reason: trimmed(LIMITS.reason, '停止の理由を入力してください'),
});
// ---------------------------------------------------------------- 送信前プレビュー
/** 実際に送る直前の形（署名・配信停止・計測リンクを含む）を 1 件分表す */
export const campaignPreviewItemSchema = z.object({
    campaignTargetId: uuid.nullable(),
    companyId: uuid,
    companyName: z.string(),
    channel: enumOf(Channels.values).nullable(),
    /** メールなら宛先、フォームなら URL */
    destination: z.string().nullable(),
    messageId: uuid.nullable(),
    messageStatus: enumOf(MessageStatuses.values).nullable(),
    state: enumOf(TargetStates.values).nullable(),
    skipReason: enumOf(SkipReasons.values).nullable(),
    subject: z.string().nullable(),
    /** 送信される本文そのもの */
    body: z.string(),
    /** HTML でも送る設定のときだけ入る */
    html: z.string().nullable(),
    ngPhraseHits: z.array(z.string()).default([]),
    /** このまま送ると起きる問題（宛先なし・未承認など） */
    warnings: z.array(z.string()).default([]),
    /** 送信対象に含まれるか */
    sendable: z.boolean(),
});
export const campaignPreviewSummarySchema = z.object({
    total: z.number().int(),
    sendable: z.number().int(),
    noMessage: z.number().int(),
    notApproved: z.number().int(),
    noDestination: z.number().int(),
    withNgPhrases: z.number().int(),
    byChannel: z.object({ form: z.number().int(), email: z.number().int() }),
});
/** プレビューの前提。差出人や計測の設定が意図どおりかを一緒に見せる */
export const campaignPreviewSenderSchema = z.object({
    driver: z.string(),
    fromAddress: z.string(),
    fromName: z.string(),
    replyTo: z.string().nullable(),
    configured: z.boolean(),
    trackOpens: z.boolean(),
    trackClicks: z.boolean(),
    htmlMail: z.boolean(),
    unsubscribeLink: z.boolean(),
    /** セーフモード中は許可ドメイン以外へ実送信しない */
    safeMode: z.boolean(),
    safeModeAllowlist: z.array(z.string()),
});
export const campaignPreviewQuerySchema = listQuerySchema.extend({
    channel: csvArray(enumOf(Channels.values)),
    /** 送信対象だけ／問題があるものだけ */
    only: z.enum(['all', 'sendable', 'blocked']).default('all'),
});
// ---------------------------------------------------------------- 効果測定
export const trackingEventSchema = z.object({
    id: uuid,
    type: enumOf(TrackingEventTypes.values),
    companyId: uuid.nullable(),
    companyName: z.string().nullable(),
    campaignId: uuid.nullable(),
    sendAttemptId: uuid.nullable(),
    url: z.string().nullable(),
    detail: z.string().nullable(),
    occurredAt: z.string(),
});
/**
 * 効果測定の集計。率の分母は「計測できる送信（HTML で実送信したメール）」で、
 * フォーム送信やセーフモードの記録は含めない。
 */
export const trackingStatsSchema = z.object({
    trackedSends: z.number().int(),
    /** 率の分母。計測できる送信を受け取った企業のユニーク数 */
    trackedCompanies: z.number().int(),
    deliveredCompanies: z.number().int(),
    openedCompanies: z.number().int(),
    clickedCompanies: z.number().int(),
    unsubscribedCompanies: z.number().int(),
    bouncedCompanies: z.number().int(),
    complainedCompanies: z.number().int(),
    openRate: z.number().nullable(),
    clickRate: z.number().nullable(),
    /** 開封した企業のうちクリックした割合 */
    clickThroughRate: z.number().nullable(),
    bounceRate: z.number().nullable(),
    /**
     * クリックされたリンク別。配信停止リンクは送信ごとの URL をまとめた 1 行（kind=unsubscribe）で、
     * 配信停止ページを開いた企業と配信停止した企業を数える（クリック率には入れない）
     */
    topLinks: z
        .array(z.object({
        url: z.string(),
        kind: z.enum(['link', 'unsubscribe']).default('link'),
        companies: z.number().int(),
        clicks: z.number().int(),
    }))
        .default([]),
});
/** あるリンクをクリックした企業（効果測定の URL 行を開いたときに出す） */
export const trackingLinkClicksSchema = z.object({
    url: z.string(),
    kind: z.enum(['link', 'unsubscribe']).default('link'),
    companies: z.array(z.object({
        id: uuid,
        name: z.string(),
        clicks: z.number().int(),
        lastClickedAt: z.string(),
        /** 配信停止リンクのとき: 配信停止まで済んだか */
        unsubscribed: z.boolean().optional(),
    })),
});
export const trackingQuerySchema = listQuerySchema.extend({
    campaignId: uuid.optional(),
    companyId: uuid.optional(),
    type: csvArray(enumOf(TrackingEventTypes.values)),
    from: z.string().optional(),
    to: z.string().optional(),
});
// ---------------------------------------------------------------- メルマガ（P1 読者基盤）
/** 公開フォームからの購読申込（ダブルオプトイン） */
export const newsletterSubscribeSchema = z.object({
    o: uuid, // 組織 ID
    email: z.string().trim().toLowerCase().email('メールアドレスの形式が正しくありません').max(200),
});
/** 管理画面からの購読者の手動追加（確認待ちを飛ばして購読中にできる） */
export const newsletterSubscriberCreateSchema = z.object({
    email: z.string().trim().toLowerCase().email('メールアドレスの形式が正しくありません').max(200),
    companyId: uuid.optional(),
});
export const NEWSLETTER_SUBSCRIBER_STATUSES = ['pending', 'confirmed', 'unsubscribed'];
export const NEWSLETTER_SUBSCRIBER_STATUS_LABELS = {
    pending: '確認待ち',
    confirmed: '購読中',
    unsubscribed: '解除済み',
};
export const newsletterSubscriberQuerySchema = listQuerySchema.extend({
    status: csvArray(z.enum(NEWSLETTER_SUBSCRIBER_STATUSES)),
    q: optionalTrimmed(200),
});
/** メルマガの号（P2 配信） */
export const newsletterCreateSchema = z.object({
    subject: trimmed(LIMITS.subject, '件名を入力してください'),
    body: trimmed(LIMITS.body, '本文を入力してください'),
});
export const newsletterUpdateSchema = newsletterCreateSchema.partial();
export const NEWSLETTER_STATUSES = ['draft', 'sending', 'sent'];
export const NEWSLETTER_STATUS_LABELS = {
    draft: '下書き',
    sending: '配信中',
    sent: '配信済み',
};
//# sourceMappingURL=messaging.js.map